import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import CodeRequestCard from '../components/CodeRequestCard';
import GameRequestCard from '../components/GameRequestCard';
import ClaimedCodeCard from '../components/ClaimedCodeCard';
import CreateCodeRequest from '../components/CreateCodeRequest';
import '../index.css';

const CodeRequests = () => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('requests');
  const [codeRequests, setCodeRequests] = useState([]);
  const [gameGroups, setGameGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState('request');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [searchInput, setSearchInput] = useState(''); // Local search input state
  const [filter, setFilter] = useState({
    platform: 'all',
    status: 'active',
    search: ''
  });

  const fetchCodeRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        status: filter.status,
        search: filter.search || undefined,
        page: currentPage,
        limit: 20
      };

      if (activeTab === 'requests') {
        params.type = 'request';
        // Fetch grouped by game for requests tab
        const response = await axios.get('/api/code-requests/grouped-by-game', { params });
        setGameGroups(response.data.data);
        setCodeRequests([]);
      } else if (activeTab === 'offers') {
        params.type = 'offer';
        // Still show individual offers (not grouped)
        const response = await axios.get('/api/code-requests', { params });
        setCodeRequests(response.data.data);
        setGameGroups([]);
        setPagination(response.data.pagination);
      } else if (activeTab === 'myposts') {
        params.userId = user?._id;
        const response = await axios.get('/api/code-requests', { params });
        setCodeRequests(response.data.data);
        setGameGroups([]);
        setPagination(response.data.pagination);
      } else if (activeTab === 'claimed') {
        // Fetch claimed codes
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/code-requests/user/claimed', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCodeRequests(response.data.data);
        setGameGroups([]);
        setLoading(false);
        return;
      }

      if (filter.platform !== 'all') {
        params.platform = filter.platform;
      }

    } catch (error) {
      console.error('Error fetching code requests:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filter, user, currentPage]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilter(prev => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    fetchCodeRequests();
  }, [activeTab, filter, fetchCodeRequests]);

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    fetchCodeRequests();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    // Reset filter and search input when changing tabs
    setSearchInput('');
    setFilter({
      platform: 'all',
      status: 'active',
      search: ''
    });
  };

  const handleCreateClick = (type) => {
    setCreateType(type);
    setShowCreateModal(true);
  };

  return (
    <div className="code-requests-page">
      <div className="code-requests-header">
        <div>
          <h1 className="code-requests-title">🎮 Request & Offer Codes</h1>
          <p className="code-requests-subtitle">
            Request game codes or offer codes to the community
          </p>
        </div>
        {isAuthenticated && (
          <div className="create-buttons">
            <button
              onClick={() => handleCreateClick('request')}
              className="create-btn request-btn"
            >
              + Request Code
            </button>
            <button
              onClick={() => handleCreateClick('offer')}
              className="create-btn offer-btn"
            >
              + Offer Code
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => handleTabChange('requests')}
        >
          📥 Browse Requests
        </button>
        <button
          className={`tab ${activeTab === 'offers' ? 'active' : ''}`}
          onClick={() => handleTabChange('offers')}
        >
          📤 Browse Offers
        </button>
        {isAuthenticated && (
          <>
            <button
              className={`tab ${activeTab === 'myposts' ? 'active' : ''}`}
              onClick={() => handleTabChange('myposts')}
            >
              📝 My Posts
            </button>
            <button
              className={`tab ${activeTab === 'claimed' ? 'active' : ''}`}
              onClick={() => handleTabChange('claimed')}
            >
              ✅ Claimed Codes
            </button>
          </>
        )}
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Platform:</label>
          <select
            value={filter.platform}
            onChange={(e) => setFilter({ ...filter, platform: e.target.value })}
            className="filter-select"
          >
            <option value="all">All Platforms</option>
            <option value="steam">Steam</option>
            <option value="epic">Epic Games</option>
            <option value="gog">GOG</option>
            <option value="xbox">Xbox</option>
            <option value="playstation">PlayStation</option>
            <option value="nintendo">Nintendo</option>
            <option value="origin">Origin</option>
            <option value="uplay">Uplay</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="filter-select"
          >
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="filter-group search-group">
          <input
            type="text"
            placeholder="Search by game name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Code Requests Grid */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      ) : activeTab === 'requests' && gameGroups.length === 0 ? (
        <div className="empty-state">
          <p>No game requests found</p>
          {isAuthenticated && (
            <button
              onClick={() => handleCreateClick('request')}
              className="create-btn-empty"
            >
              + Request Code
            </button>
          )}
        </div>
      ) : activeTab === 'requests' && gameGroups.length > 0 ? (
        <div className="code-requests-grid">
          {gameGroups.map((gameGroup, index) => (
            <GameRequestCard
              key={`${gameGroup.gameName}-${gameGroup.platform}-${index}`}
              gameData={gameGroup}
            />
          ))}
        </div>
      ) : codeRequests.length === 0 ? (
        <div className="empty-state">
          <p>
            {activeTab === 'claimed' 
              ? 'You haven\'t claimed any codes yet' 
              : `No ${activeTab === 'offers' ? 'offers' : 'posts'} found`}
          </p>
          {isAuthenticated && activeTab !== 'myposts' && activeTab !== 'claimed' && (
            <button
              onClick={() => handleCreateClick(activeTab === 'offers' ? 'offer' : 'request')}
              className="create-btn-empty"
            >
              {activeTab === 'offers' ? '+ Offer Code' : '+ Request Code'}
            </button>
          )}
        </div>
      ) : (
        <div className="code-requests-grid">
          {activeTab === 'claimed' ? (
            codeRequests.map((claimedCode) => (
              <ClaimedCodeCard
                key={claimedCode._id}
                claimedCode={claimedCode}
              />
            ))
          ) : (
            codeRequests.map((request) => (
              <CodeRequestCard
                key={request._id}
                codeRequest={request}
                onUpdate={fetchCodeRequests}
              />
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.pages} ({pagination.total} total)
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(pagination.pages, prev + 1))}
            disabled={currentPage === pagination.pages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateCodeRequest
          type={createType}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
};

export default CodeRequests;
